import RenderManager from "../rendering/render-manager";
import ObjectManager from "../objects/object-manager";
import AssessmentManager from "../assessment/assessment-manager";
import FaceSorter from '../geometry/faceSorting';
import generateAnimatedBoundingBox from "../utils/generate-animated-bbox";

const MISSING_GEOMETRY_DELETE_OBJECTS = true;

/**
 * Loads the geometry.
 * @param {Object} props Parameters.
 * @param {string} props.uuid Geometry UUID.
 * @param {Object} props.data Geometry data.
 * @param {Object} props.loaders Loaders.
 * @param {boolean} props.isAsset Is this an asset.
 */
async function loadGeometry({ uuid, data, loaders, isAsset }) {
    let geometry = null;
    let isMesh = false;

    switch (data.type) {
        case "Sphere": geometry = await createSphereGeometry(data); break;
        case "Cube": geometry = await createCubeGeometry(data); break;
        case "Arrow": geometry = await createArrowGeometry(data); break;
        case "Plane": geometry = await createPlaneGeometry(data); break;
        case "Ring": geometry = await createRingGeometry(data); break;
        case "IconHotspot": geometry = await createSpriteGeometry(data); break;
        default:
            geometry = await (isAsset ? loadMeshGeometry(uuid, data, loaders) : createPlaceholderGeometry(data));
            isMesh = true;
            break;
    }


    geometry = isAsset && isMesh ? geometry.geometry : geometry;

    finalizeGeometry({ geometry, uuid, name: data.name });

    return { uuid, geometry };
}

/**
 * Adds geometry to queue.
 * @param {Object} props Parameters.
 * @param {Object} props.data Geometry data.
 * @param {Object} props.queue Asset queue.
 * @param {string} props.key Geometry UUID.
 */
function collectGeometry({ data, queue, key }) {
    queue.addItem(0, {
        uuid: key,
        data: data.assets.geometries[key],
        type: "geometry"
    }, params => loadQueueGeometry(params));
}

/**
 * Checks if geometry is in use.
 * @param {Object} props Parameter.
 * @param {String} props.uuid Geometry UUID.
 * @param {Object} props.data Project data.
 * @return {boolean}
 */
function isGeometryInUse({ uuid, data }) {
    if (Object.keys(data.assets.objects).some(key => {
        return data.assets.objects[key].geometry === uuid;
    })) return true;

    console.warn(`Geometry ${data.assets.geometries[uuid].name} (${uuid}) is unused, removing.`);

    return false;
}

export default loadGeometry;
export { loadGeometry, collectGeometry, isGeometryInUse };

/**
 * Finalizes geometry loading.
 * @param {Object} props Parameter.
 * @param {THREE.BufferGeometry} props.geometry Loaded geometry.
 * @param {string} props.uuid Geometry UUID.
 * @param {string} props.name Geometry name.
 */
function finalizeGeometry({ geometry, uuid, name }) {
    geometry.uuid = uuid;
    geometry.name = name;
}

/**
 * Creates sphere geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createSphereGeometry(data) {
    return new THREE.SphereBufferGeometry(
        data.radius,
        data.widthSegments,
        data.heightSegments
    );
}

/**
 * Creates cube geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createCubeGeometry(data) {
    return new THREE.BoxBufferGeometry(
        data.width,
        data.height,
        data.depth
    );
}

/**
 * Creates arrow geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createArrowGeometry(data) {
    return new THREE.ArrowBufferGeometry(
        data.width,
        data.height,
        data.depth,
        data.arrowWidth,
        data.arrowHeight
    );
}

/**
 * Creates plane geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createPlaneGeometry(data) {
    return new THREE.PlaneBufferGeometry(
        data.width,
        data.height,
        data.widthSegments,
        data.heightSegments
    );
}

/**
 * Creates ring geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createRingGeometry(data) {
    return new THREE.CustomRingBufferGeometry(
        data.innerRadius,
        data.outerRadius || data.outererRadius,
        data.thetaSegments
    );
}

/**
 * Creates sprite geometry.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createSpriteGeometry(data) {
    return new THREE.PlaneBufferGeometry(1, 1, 1, 1);
}

/**
 * Creates placeholder geometry used for the system to not crash while LOD is loading.
 * @param {Object} data Geometry parameters.
 * @return {Promise}
 */
async function createPlaceholderGeometry(data) {
    const geometry = new THREE.BufferGeometry();

    geometry.addAttribute("position", new THREE.Float32BufferAttribute(new Float32Array([]), 3));
    geometry.addAttribute("skinWeight", new THREE.Float32BufferAttribute(new Float32Array([]), 4));
    geometry.isPlaceholder = true;

    return geometry;
}

/**
 * Loads geometry from queue.
 * @param {Object} props Parameters.
 * @param {Object} props.assetMap Asset map.
 * @param {Object} props.loaders Loaders.
 * @param {Object} props.assets Assets.
 * @param {Object} props.item Item that loaded.
 * @param {Object} props.data Geometry data.
 * @return {Promise}
 */
function loadQueueGeometry({ assetMap, loaders, assets, item, data }) {
    return new Promise((resolve, reject) => {

        new Promise((resolve, reject) => {
            if (!(item.data.path in assetMap)) {
                assetMap[item.data.path] = new Promise((_resolve, _reject) => {
                    loadMeshGeometry(item.uuid, item.data, loaders, null)
                        .then(e => _resolve(e))
                        .catch(e => _reject(e));
                });
            }

            assetMap[item.data.path]
                .then(geometryData => {
                    loadMeshGeometry(item.uuid, item.data, loaders, geometryData.data)
                        .then(e => resolve(e))
                        .catch(reject);
                })
                .catch(reject);

        }).then(geometryData => {
            const geometry = geometryData.geometry;

            try {
                finalizeGeometry({ geometry, uuid: item.uuid, name: item.data.name });
                updateObjects({ uuid: item.uuid, data, assets, geometry });
                resolve(geometryData);
            } catch (e) {
                reject(e);
            }
        }, error => {
            if (MISSING_GEOMETRY_DELETE_OBJECTS) {
                if (error.target instanceof XMLHttpRequest && error.target.status === 404) {
                    deleteObjects({ uuid: item.uuid, data, assets });
                }
            }
            reject(error);
        });
    });
}

/**
 * Updates objects that used this geometry.
 * @param {Object} props Parameters.
 * @param {string} props.uuid UUID of the geometry.
 * @param {Object} props.data Project data.
 * @param {Object} props.assets Assets.
 * @param {THREE.BufferGeometry} props.geometry Geometry that has loaded.
 */
function updateObjects({ uuid, data, assets, geometry }) {
    assets.geometries[uuid] = geometry;

    let normalizedBoneWeights = false;

    Object.keys(data.assets.objects).forEach(key => {
        if (data.assets.objects[key].geometry === uuid && assets.objects[key].geometry.isPlaceholder) {
            const mesh = assets.objects[key];

            mesh.geometry = geometry;
            mesh.updateMorphTargets();

            if (!normalizedBoneWeights && mesh.isSkinnedMesh) {
                mesh.normalizeSkinWeights();
                normalizedBoneWeights = true;

                generateAnimatedBoundingBox(mesh);
            }

            addObjectMissingMaterials(mesh, geometry);

            RenderManager.raycaster.makeTrackable(mesh);
        }
    });

    AssessmentManager.assessments.forEach(ass => {
        if (!ass.isPlopAssessment) return;

        if (ass.object.geometry.uuid === uuid)
            ass.updateGeometry(geometry);
    });
}

/*
 * Adds the first material to all material slots that are referenced by
 * geometry groups so that the pieces whose material is missing would
 * still get rendered. This is a workaround for content problem since a
 * properly made model should have a material assigned to each geometry
 * group that is supposed to be rendered.
 */
function addObjectMissingMaterials(object, geometry) {
    if (geometry.groups && object.material instanceof Array) {
        const material0 = object.material[0];

        for (let group of geometry.groups) {
            if (!object.material[group.materialIndex]) {
                object.material[group.materialIndex] = material0;
            }
        }
    }
}

/**
 * Deletes objects that used this geometry.
 * @param {Object} props Parameters.
 * @param {string} props.uuid UUID of the geometry.
 * @param {Object} props.data Project data.
 * @param {Object} props.assets Assets.
 */
function deleteObjects({ uuid, data, assets }) {
    for (let key in data.assets.objects) {
        if (data.assets.objects[key].geometry === uuid) {
            ObjectManager.deleteObjectFromTour(assets.objects[key]);
        }
    }
}

/**
 * Returns the URL of geometry file based on geometry ratio setting
 * @param {Object} props Parameters.
 * @param {string} props.uuid UUID of the geometry.
 * @param {Object} props.data Project data.
 * @param {Object} props.assets Assets.
 */
function getGeometryUrl({ geometryData, browserCapabilities }) {
    const levels = [[1, geometryData.path]];
    if (geometryData.levels) {
        for (let { ratio, path } of geometryData.levels) {
            levels.push([ratio, path]);
        }
    }

    const targetRatio = browserCapabilities.supports.geometries.ratio;
    let closestPath = null, closestDiff = Infinity;

    for (let [ratio, path] of levels) {
        const diff = Math.abs(targetRatio-ratio);
        if (diff <= closestDiff) {
            closestPath = path;
            closestDiff = diff;
        }
    }

    return closestPath;
}

/**
 * Loads mesh geometry from file. Supports DRACO and GLTF.
 * @param {string} uuid UUID of the geometry.
 * @param {Object} data Geometry data.
 * @param {Object} loaders Loaders.
 * @param {Object} asset Assets.
 */
export function loadMeshGeometry(uuid, data, loaders, asset) {
    return new Promise((resolve, reject) => {
        const isGLTF = data.path.match(/.glb$|.gltf$/);

        function loadDraco(path) {
            if (!asset) {
                loaders.draco().then(dracoLoader => {
                    dracoLoader.load(path, geometry => {
                        resolve({ geometry });
                    }, undefined, reason => {
                        console.error("Failed to resolve geometry:", reason);
                        reject(reason);
                    });
                });
            } else {
                resolve({ geometry: asset });
            }
        }

        function loadGeometryFromGLTF(path) {
            function onLoad(nodes) {
                let mesh = null;
                let geometry = null;
                const node = nodes.scene.getObjectByName(uuid);

                if (!!node) {
                    mesh = node;
                } else {
                    mesh = nodes.scene.children[0];
                }

                geometry = mesh.geometry;

                restoreGeometryMaterialIndices(mesh);
                FaceSorter.addGeometry(geometry);

                resolve({ data: nodes, geometry });
            }

            if (!asset) {
                loadGLTF(loaders.GLTF, path, nodes => {
                    onLoad(nodes);
                }, reason => {
                    console.error(`Failed to resolve '${path}' geometry: ${reason}`)
                    reject(reason);
                });
            } else { onLoad(asset); }
        }

        const path = getGeometryUrl({
            geometryData: data,
            browserCapabilities: Q3.browserCapabilities,
        });

        if (isGLTF) loadGeometryFromGLTF(Q3.s3Url + path);
        else loadDraco(Q3.s3Url + path);
    });
}

const geometryMatIndRestored = new WeakSet();

/**
 * Assigns material indices (as indicated by glTF material names) to geometry groups
 * @param {THREE.Mesh} mesh Mesh whose geometry groups we want to modify according to names of its materials
 */
function restoreGeometryMaterialIndices(mesh) {
    const geometry = mesh.geometry;

    if (!geometryMatIndRestored.has(geometry) && mesh.material) {
        if (mesh.material instanceof Array) {
            for (let group of geometry.groups) {
                let material = mesh.material[group.materialIndex];
                if (material) {
                    let name = material.name;
                    let index = Number(name);
                    if (String(index) === name) {
                        group.materialIndex = index;
                    }
                }
            }
        } else {
            let name = mesh.material.name;
            let index = Number(name);
            if (String(index) === name) {
                if (geometry.groups && geometry.groups.length > 0) {
                    for (let group of geometry.groups) {
                        group.materialIndex = index;
                    }
                } else {
                    let indices = geometry.getIndex();
                    let count;
                    if (indices) {
                        count = indices.count;
                    } else {
                        let position = geometry.getAttribute('position');
                        count = position.count;
                    }
                    geometry.addGroup(0, count, index);
                }
            }
        }

        geometryMatIndRestored.add(geometry);
    }
}

/**
 * Loads GLTF from file.
 * @param {THREE.GLTFLoader} loaderGLTF GLTF loader instance.
 * @param {string} path Path to the GLTF.
 * @param {Function} onLoad On loaded callback.
 * @param {Function} onError On error callback.
 */
const loadGLTF = (function () {
    // multiple geometries can be hold in a single gltf and each of them will try to load it
    // this function will prevent from loading the same file multiple times
    const gltfPromises = {};

    return function (loaderGLTF, path, onLoad, onError) {
        let promise = gltfPromises[path];

        if (!promise) {
            promise = new Promise(function (resolve, reject) {
                loaderGLTF.load(path, x => resolve(x), undefined, reject)
            });
            gltfPromises[path] = promise;
        }

        promise.then(onLoad).catch(onError);
    };
})();